import React from 'react';

import PropTypes from 'prop-types';

import {Stack, Typography, Avatar, Box} from 'views/components';
import {AccessTime, LocationOnOutlined} from 'views/icons';
import {Translate, translate} from 'views/module';
import VerifiedLabel from '../components/VerifiedIcon';

const HeaderSection = (props) => {
  const {
    questions,
  } = props;

  const name = `${questions.firstName} ${questions.lastName}`;
  const personalBio = questions.personalBio;
  const motivation = questions.caregiverMotivation;
  const profilePhotoSrc = questions.profilePhoto?.[0]?.src;
  const city = questions.city;
  const province = questions.province;
  const languages = questions.languages || [];
  const certificates = questions.certificates || [];

  const caregiverType = React.useMemo(() => {
    const cgTypesHierarchy = [
      'RN',
      'NURSING_STUDENT',
      'DSW',
      'PSW',
      'PAB',
      'CNA',
      'RPN',
      'RNA',
      'HCA',
      'PSW_STUDENT',
      'CSW',
      'SSW',
    ];
    for (let i = 0; i < cgTypesHierarchy.length; i++) {
      if (certificates.some((cert) => cert.type === cgTypesHierarchy[i])) {
        return cgTypesHierarchy[i];
      }
    }
    return 'UNACCREDITED';
  }, []);

  const getTranslatedLanguageName = (langNameKey) => {
    let langName = translate(`caregiverProfile.Header.AvailableLanguages.${langNameKey}`);
    if (langName === 'Missing translation') {
      langName = langNameKey;
    }
    return langName;
  };

  return (
    <Stack
      direction={{xs: 'column', sm: 'row'}}
      spacing={{xs: 1, sm: 2}}
      alignItems={{xs: 'center', sm: 'flex-start'}}
      width='100%'
    >
      {/* Profile Photo */}
      <Avatar
        src={profilePhotoSrc}
        alt={name}
        sx={{
          'width': {xs: 72, sm: 96, md: 128, lg: 152},
          'height': {xs: 72, sm: 96, md: 128, lg: 152},
          'border': 3,
          'borderColor': 'grey.400',
          'bgcolor': 'grey.400',
          'fontSize': 48,
          '& img': {
            overflowClipMargin: 'unset',
          },
        }}
      >
      </Avatar>
      <Stack alignItems="flex-start" sx={{flex: 1}} spacing={0.5}>
        {/* Name and Verified Icon */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h4" fontWeight="bold">{name}</Typography>
          <VerifiedLabel/>
        </Stack>
        {/* Caregiver Type */}
        <Typography variant="h6" color="grey.700" fontWeight={400}>
          <Translate expression={`caregiverProfile.Header.CaregiverType.${caregiverType}`}/>
        </Typography>
        {/* Location & Response Time */}
        <Stack direction="row" justifyContent="flex-start" spacing={2}>
          <Stack direction="row" alignItems="center">
            <LocationOnOutlined sx={{color: 'text.secondary', fontSize: 'medium'}} />
            <Typography variant="body2" color="text.secondary" sx={{ml: 0.5}}>
              {city}{`, ${province}`}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center">
            <AccessTime sx={{color: 'text.secondary', fontSize: 'medium'}} />
            <Typography variant="body2" color="text.secondary" sx={{ml: 0.5}}>
              <Translate expression='caregiverProfile.Header.ResponseTime'/>
            </Typography>
          </Stack>
        </Stack>
        {/* Personal Bio & Motivation */ }
        <Stack sx={{p: 0.5}}>
          <Typography variant="body1" color="text.primary" fontWeight="bold">
            <Translate expression='caregiverProfile.Header.PersonalBioSubtitle' />
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{pb: 0.5}}>
            {personalBio}
          </Typography>

          <Typography variant="body1" color="text.primary" fontWeight="bold">
            <Translate expression='caregiverProfile.Header.CaregiverMotivationSubtitle'/>
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{pb: 0.5}}>
            {motivation}
          </Typography>
        </Stack>
        {/* Languages */}
        {
          languages.length > 0 &&
            <Stack>
              <Typography variant="subtitle2" sx={{mb: 1}}>
                <Translate expression='caregiverProfile.Header.Languages' />
              </Typography>
              <Stack
                direction="row"
                justifyContent="flex-start"
                gap={1}
                rowGap={1}
                flexWrap="wrap"
              >
                {
                  languages.map((langObj) => (
                    <Box
                      key={langObj.name}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: 'white',
                        borderRadius: 2,
                        border: 1,
                        borderColor: 'grey.400',
                        py: 0.5,
                        px: 1,
                      }}
                    >
                      <Typography variant="caption" color="black">
                        {`${getTranslatedLanguageName(langObj.name)} (${translate(`caregiverProfile.Header.Fluency.${langObj.fluency}`)})`}
                      </Typography>
                    </Box>
                  ))
                }
              </Stack>
            </Stack>
        }
      </Stack>
    </Stack>
  );
};

HeaderSection.propTypes = {
  questions: PropTypes.object.isRequired,
};

export default HeaderSection;
